import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Card, Form, Result, Typography } from 'antd';
const { Title, Text, Paragraph } = Typography;

import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { useThemeLabel } from 'client/components/schema/config/ClientConfigModule';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { LinkButton } from 'client/ui/link/LinkButton';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import {
  citizenRequestResetValidator,
  ICitizenRequestResetDto
} from 'common/dto/CitizenDto';
import { useTranslation } from 'react-i18next';

interface Params {
  id: string;
}

export interface UserForgotPasswordPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina di attivazione (conferma email) di un utente con email e password (senza SPID)
 */
export function UserForgotPasswordPage(props: UserForgotPasswordPageProps) {
  const { t } = useTranslation();

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.forgot-password.breadcrumb', 'Password Dimenticata')
  });

  const [requestReset, requestResetState] = useApiMutation(
    CitizenApi.requestReset,
    {}
  );

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };
  return (
    <FrontLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {requestResetState.response ? (
            <Result
              title={t(
                'user.forgot-password.response.success.title',
                'Ti abbiamo inviato una email'
              )}
              subTitle={t(
                'user.forgot-password.response.success.sub-title',
                'Segui il link che hai ricevuto via email per reimpostare la password'
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/utente/login"
                  children={t(
                    'user.forgot-password.response.success.button-text',
                    'Vai al Login'
                  )}
                />
              }
            />
          ) : (
            <Formik<ICitizenRequestResetDto>
              enableReinitialize
              initialValues={{} as any}
              validate={schemaToValidator(citizenRequestResetValidator)}
              validateOnChange={false}
              onSubmit={async rawValues => {
                await requestReset({ data: { input: rawValues } });
              }}
            >
              {({ isSubmitting, submitForm, dirty }) => (
                <FormikForm {...layout}>
                  {/* Form Utente */}
                  <Form.Item {...tailLayout}>
                    <Title level={3}>
                      {t(
                        'user.forgot-password.title',
                        'Hai dimenticato la password?'
                      )}
                    </Title>
                    <Paragraph>
                      {t(
                        'user.forgot-password.text',
                        `Inserisci l’email con cui ti sei registrato. Riceverai via email il link per reimpostare la password.`
                      )}
                    </Paragraph>
                  </Form.Item>

                  {requestResetState.error && (
                    <Form.Item {...tailLayout}>
                      <Alert
                        message={
                          <>
                            {getNetworkErrorMessage(
                              requestResetState.error,
                              t(
                                'user.forgot-password.error-message.title',
                                `L'email inserita non è registrata sul portale.`
                              )
                            )}
                            <br />
                            {t(
                              'user.forgot-password.error-message.text',
                              `Controlla che l'email sia corretta; se è giusta e non hai ancora un'utenza puoi`
                            )}{' '}
                            <Link to="/utente/registrazione">
                              {t(
                                'user.forgot-password.error-message.link',
                                `registrarti seguendo questo link`
                              )}
                            </Link>
                            .
                          </>
                        }
                        type="warning"
                        showIcon
                        closable
                      />
                    </Form.Item>
                  )}

                  <TextInputItem name="email" label="Email" />

                  <Form.Item {...tailLayout}>
                    <Button
                      htmlType="submit"
                      type="primary"
                      style={{ width: '100%' }}
                    >
                      {t(
                        'user.forgot-password.button-text',
                        `Inviami un'email`
                      )}
                    </Button>
                  </Form.Item>
                </FormikForm>
              )}
            </Formik>
          )}
        </Card>
      </div>
    </FrontLayout>
  );
}
